import type { RttsStatus } from '../types';

export type ProgressState = {
  stageKey: RttsStatus | 'unknown';
  /** Studio-themed status line. */
  copy: string;
  /** Approximate bar fill 0–100. */
  barPct: number;
  isTerminal: boolean;
  isError: boolean;
};

const FAIL_COPY: Record<string, string> = {
  source_unavailable: 'We couldn’t pull that tape. Try again when the VOD is available.',
  detect_failed: 'We couldn’t find highlights on this tape. Try another game.',
  render_failed: 'The cut stalled in the studio. Try again when you’re ready.',
  entitlement: 'Scoutlytics Studio isn’t enabled for this league.',
  feature_disabled: 'Scoutlytics Studio is temporarily unavailable.',
};

const STAGE: Record<
  RttsStatus,
  Omit<ProgressState, 'stageKey'> & { stageKey: RttsStatus }
> = {
  downloading: {
    stageKey: 'downloading',
    copy: 'Pulling the tape…',
    barPct: 10,
    isTerminal: false,
    isError: false,
  },
  source_ready: {
    stageKey: 'source_ready',
    copy: 'Tape secured',
    barPct: 20,
    isTerminal: false,
    isError: false,
  },
  detecting: {
    stageKey: 'detecting',
    copy: 'Finding the highlights…',
    barPct: 50,
    isTerminal: false,
    isError: false,
  },
  detect_ready: {
    stageKey: 'detect_ready',
    copy: 'Highlights locked',
    barPct: 75,
    isTerminal: false,
    isError: false,
  },
  rendering: {
    stageKey: 'rendering',
    copy: 'Cutting your reel…',
    barPct: 90,
    isTerminal: false,
    isError: false,
  },
  delivered: {
    stageKey: 'delivered',
    copy: 'Your reel is ready',
    barPct: 100,
    isTerminal: true,
    isError: false,
  },
  failed: {
    stageKey: 'failed',
    copy: 'Something went wrong in the studio.',
    barPct: 0,
    isTerminal: true,
    isError: true,
  },
};

function isRttsStatus(value: string): value is RttsStatus {
  return Object.prototype.hasOwnProperty.call(STAGE, value);
}

/**
 * Pure progress-state derivation for the RTTS studio wait experience.
 * Keep branching here — React components only render the returned state.
 */
export function deriveProgressState(
  status: RttsStatus | string,
  errorCode?: string | null
): ProgressState {
  if (!isRttsStatus(status)) {
    return {
      stageKey: 'unknown',
      copy: 'Working…',
      barPct: 35,
      isTerminal: false,
      isError: false,
    };
  }

  if (status === 'failed') {
    const code = (errorCode ?? '').trim();
    return {
      ...STAGE.failed,
      copy: (code && FAIL_COPY[code]) || STAGE.failed.copy,
    };
  }

  return { ...STAGE[status] };
}
